import React, { useState } from 'react';
import ServiceHistoryTable from './ServiceHistoryTable';
import ExpandableText from '../common/ExpandableText';
import { formatPhoneNumber } from '../../helpers/phoneFormatter';

const sxStyles = {
    height: 450,
    fontSize: '1em',
    '& .MuiDataGrid-columnHeaders': {
        minHeight: 'fit-content !important',
    },
    '& .MuiDataGrid-row.Mui-selected': {
        backgroundColor: 'inherit',
    },
    '& .MuiDataGrid-row.Mui-selected:hover': {
        backgroundColor: 'inherit',
    },
    '.MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel': {
        marginTop: '1em',
        marginBottom: '1em',
    },
    '.MuiDataGrid-virtualScroller': {
        overflowY: 'auto',
        overflowX: 'auto',
    },
};

const renderExpandableCell = (params) => {
    return (
        <ExpandableText
            text={params.value || ''}
            width={params.colDef.computedWidth}
            isHeader={false}
        />
    );
};

const renderExpandableHeader = (params) => {
    return (
        <ExpandableText
            text={params.colDef.headerName}
            width={params.colDef.computedWidth}
            isHeader={true}
        />
    );
};

const columns = [
    {
        headerName: 'Customer Name',
        field: 'customerName',
        sortable: true,
        flex: 1.2,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
    },
    {
        headerName: 'Vehicle Year/Make/Model',
        field: 'yearMakeModel',
        flex: 1.5,
        sortable: true,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
    },
    {
        headerName: 'License Plate',
        field: 'licensePlate',
        flex: 0.8,
        sortable: true,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
    },
    {
        headerName: 'State',
        field: 'state',
        flex: 0.7,
        sortable: true,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
    },
    {
        headerName: 'Phone',
        field: 'phone',
        flex: 1,
        sortable: true,
        renderCell: (params) => renderExpandableCell(formatPhoneNumber(params)),
        renderHeader: renderExpandableHeader,
    },
    {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        sortable: true,
        renderCell: renderExpandableCell,
        renderHeader: renderExpandableHeader,
    },
    {
        headerName: 'Work Order ID',
        field: 'workOrderId',
        sortable: true,
    },
    {
        headerName: 'Store ID',
        field: 'storeId',
        sortable: true,
    },
];

const VehicleDataGrid = ({
    vehicleData,
    nextBestAction,
    rowsPerPage,
    criteriaSelected,
}) => {
    const [selectedRow, setSelectedRow] = useState(null);
    const [isValid, setIsValid] = useState(true);
    const storedSortModel = JSON.parse(localStorage.getItem('SHorderBy')) || [];
    const [sortModel, setSortModel] = useState(storedSortModel);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: rowsPerPage,
    });

    const data = vehicleData.map((row, index) => ({
        id: row.vehicleId || row.id || index,
        ...row,
    }));

    const validateVehicle = (row) => {
        setSelectedRow(row);
        const vehicleIsValid = isVehicleValid(criteriaSelected, row);
        setIsValid(vehicleIsValid);

        if (vehicleIsValid) {
            nextBestAction({
                customerName: row.customerName,
                yearMakeModel: row.yearMakeModel,
                licensePlate: row.licensePlate,
                state: row.state,
                phone: row.phone,
                email: row.email,
                workOrderId: row.workOrderId,
                storeId: row.storeId,
                id: row.id,
            });
        }
    };

    const isVehicleValid = (
        criteriaSelected,
        { state, licensePlate, workOrderId, storeId }
    ) => {
        if (criteriaSelected !== 4) {
            return state !== 'Unknown' && licensePlate !== 'Unknown';
        }
            return workOrderId !== '' && storeId !== '';
    };

    const handleSortChange = (newSortModel) => {
        setSortModel(newSortModel);
        localStorage.setItem('SHorderBy', JSON.stringify(newSortModel));
    };

    const handlePaginationModelChange = (newModel) => {
        setPaginationModel(newModel);
    };

    const handleRowClassName = (params) => {
        return selectedRow?.id === params.id ? 'selected-row' : '';
    };

    return (
        <div className="vehicle-container">
            <div className="vehicle-table-container">
                <ServiceHistoryTable
                    columns={columns}
                    data={data}
                    onRowClick={validateVehicle}
                    onSortChange={handleSortChange}
                    sortModel={sortModel}
                    columnVisibilityModel={{
                        workOrderId: false,
                        storeId: false,
                    }}
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    rowsPerPageOptions={[5, 10, 20]}
                    getRowClassName={handleRowClassName}
                    rowHeight={100}
                    headerHeight={100}
                    sx={sxStyles}
                />
            </div>
            {!isValid && (
                <div className="vehicle-not-valid">
                    <label id="errorLb">
                        This record has missing information and cannot be selected
                    </label>
                </div>
            )}
        </div>
    );
};

export default VehicleDataGrid;
